import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ContentWrapper, PageWrapper, ReservationSection, TermsRow} from "./styles";
import {MainButton} from '../../../../components/common';
import {PoweredByPickapier} from "../PoweredByPickapier";
import {useHistory, useRouteMatch} from "react-router-dom";
import {ReservationHeader} from "../ReservationHeader";
import {ReservationSummary} from "../ReservationSummary";
import {useDispatch, useSelector} from "react-redux";
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from "@stripe/stripe-js";
import i18n from "../../../../i18n";
import * as Yup from "yup";
import {useFormik} from "formik";
import {
    selectMarina,
    selectMarinaData,
    selectRedirectUrl,
    selectRedirectUrlProvided
} from "../../../../store/slices/app/selectors";
import {
    selectBoaterMail,
    selectReservationTimerExtended,
    selectFinished,
    selectForceTimer,
    selectHullType,
    selectIsCardError,
    selectNatureOfTravel,
    selectPayment,
    selectReservationDetails,
    selectReservationEndDate,
    selectReservationProposal,
    selectReservationStartDate,
    selectVesselBeam,
    selectVesselDraft,
    selectVesselLength,
    selectVesselType,
    selectVesselsSameDimensions,
    selectError,
    selectContractId,
    selectVesselName,
    selectIsLoggedInStep1,
    selectIsRequest,
    selectVessel,
    selectUsePopeyeBalance
} from "../../../../store/slices/reservation/selectors";
import {ReservationForm} from "../ReservationForm";
import {Trans, useTranslation} from "react-i18next";
import {reservationActions} from "../../../../store/slices/reservation/reservationSlice";
import {ReservationPayment} from "../ReservationPayment";
import {ReservationSuccessDialog} from "../ReservationSuccessDialog";
import {ReservationFailureDialog} from "../ReservationFailureDialog";
import {formRules} from "../style";
import {ReservationRoutes as Routes} from "../../routes";
import {getReservationValidationSchema, getDocumentsValidationSchema} from "../../../../utils/validations";
import {InstantBookingRequirement, InstantBookingAdditionalRequirement} from "../../../../settings/enums";
import {formatPriceNoCurrency} from "../../../../utils/format";
import {AuthContext} from "../../../../context/AuthProvider";

const apiKey = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = loadStripe(`${apiKey}` || '');
const timeout = process.env.REACT_APP_SESSION_TIMEOUT_MINUTES ?
    parseFloat(process.env.REACT_APP_SESSION_TIMEOUT_MINUTES) : 10;

const stripeElementsOptions = clientSecret => ({
    fonts: [ { cssSrc: 'https://fonts.googleapis.com/css?family=Lato:300,300i,400,500,600' } ],
    locale: i18n.language || 'en',
    clientSecret,
    appearance: {
        theme: "none",
        rules: formRules
    },
});

const Reservation = () => {
    const history = useHistory();
    const match = useRouteMatch();
    const marina = useSelector(selectMarina);
    const marinaData = useSelector(selectMarinaData);
    const startDate = useSelector(selectReservationStartDate);
    const endDate = useSelector(selectReservationEndDate);
    const proposal = useSelector(selectReservationProposal);
    const usePopeyeBalance = useSelector(selectUsePopeyeBalance);
    const vesselLength = useSelector(selectVesselLength);
    const vesselBeam = useSelector(selectVesselBeam);
    const vesselDraft = useSelector(selectVesselDraft);
    const vesselType = useSelector(selectVesselType);
    const hullType = useSelector(selectHullType);
    const vesselName = useSelector(selectVesselName);
    const natureOfTravel = useSelector(selectNatureOfTravel);
    const reservationDetails = useSelector(selectReservationDetails);
    const isRequest = useSelector(selectIsRequest);
    const payment = useSelector(selectPayment);
    const finished = useSelector(selectFinished);
    const redirectUrl = useSelector(selectRedirectUrl);
    const redirectUrlProvided = useSelector(selectRedirectUrlProvided);
    const mail = useSelector(selectBoaterMail);
    const cardError = useSelector(selectIsCardError);
    const forceTimer = useSelector(selectForceTimer);
    const contract_id = useSelector(selectContractId);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const submitRef = useRef();
    const extended = useSelector(selectReservationTimerExtended);
    const { user: boater, loggedIn } = useContext(AuthContext);
    const vessels = useSelector(selectVesselsSameDimensions);
    const reservationError = useSelector(selectError);
    const [focusOnFirst, setFocusOnFirst] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [updatReservation, setUpdateReservation] = useState(false);
    const isLoggedInStep1 = useSelector(selectIsLoggedInStep1);
    const selectedVessel = useSelector(selectVessel);
    const { refreshData  } = useContext(AuthContext);

    const requirements = useMemo(() => ({
        vesselType:         marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].initial_inquiry?.includes(InstantBookingRequirement.VesselType),
        hullType:           marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].initial_inquiry?.includes(InstantBookingRequirement.VesselHullType),
        natureOfTravel:     marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].initial_inquiry?.includes(InstantBookingRequirement.PrivateOrCommercial),
    }), [marina?.marina_settings, contract_id]);

    const additionalRequirements = useMemo(() => ({
        phone:       marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.Phone),
        dateOfBirth: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.DateOfBirth),
        address:    marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.Address),
        postCode:   marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.PostalCode),
        country:    marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.Country),
        vesselType: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.VesselType),
        hullType:   marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.VesselHullType),
        waterElectricity: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.WaterElectricity),
        natureOfTravel: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.PrivateCommercial),
        homeMarina: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.HomeMarinaName),
        vesselType:  marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.VesselType),
        hullType:    marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"].instant_booking?.includes(InstantBookingAdditionalRequirement.VesselHullType),
        liveAboard: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"]?.instant_booking?.includes(InstantBookingAdditionalRequirement.LiveAboard),
        vessel_license: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"]?.instant_booking?.includes(InstantBookingAdditionalRequirement.License),
        vessel_insurance: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"]?.instant_booking?.includes(InstantBookingAdditionalRequirement.Insurance),
        skipper_id: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"]?.instant_booking?.includes(InstantBookingAdditionalRequirement.SkipperId),
        vessel_photo: marina?.marina_settings?.marina_requirements?.[contract_id? "long" : "short"]?.instant_booking?.includes(InstantBookingAdditionalRequirement.Photo),
        existingVesselName: !!( !isLoggedInStep1 && boater && vessels?.length)
    }), [marina?.marina_settings, boater, vessels, contract_id]);

    useEffect(() => {
        if(focusOnFirst) {
            setSubmitted(true);
        }
    }, [focusOnFirst]);

    useEffect(() => {
        if(!boater || (!boater.is_guest && !loggedIn)) return;

        if(window.location.pathname.endsWith(`${Routes.Finished}`) || window.location.hash.endsWith(`${Routes.Finished}`)) {
            const params = new URLSearchParams(window.location.search);
            const redirectStatus = params.get('redirect_status');
            if(redirectStatus === 'succeeded') {
                dispatch(reservationActions.finishReservation());
            } else {
                dispatch(reservationActions.setCardError(true));
                dispatch(reservationActions.proceedTimer());
                dispatch(reservationActions.setForceTimer(true));
            }
        }
    }, [window.location.pathname, boater, loggedIn]);

    useEffect(() => {
        console.log("reservationErrors", reservationError);
        console.log("finished:: ", finished);
    }, [reservationError, finished]);

    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            mail: '',
            mailConfirmation: '',
            address: '',
            addressLineTwo: '',
            postCode: '',
            country: null,
            vesselName: vesselName || null,
            existingVesselName: vesselName || null,
            vesselType:vesselType || null,
            hullType: hullType || null,
            natureOfTravel: null,
            waterElectricity: false,
            homeMarinaId: undefined,
            power: null,
            notes: '',
            liveAboard: undefined,
            vessel_license: null,
            vessel_insurance: null,
            skipper_id: null,
            vessel_photo: null
        },
        validateOnBlur: false,
        validateOnChange: true,
        validateOnMount: true,
        validationSchema: Yup.object().shape({...getReservationValidationSchema(t, additionalRequirements), ...getDocumentsValidationSchema(t,additionalRequirements)}),
        onSubmit: values => {
            if (updatReservation) {
                setUpdateReservation(false);
            }
            let attachment_ids=[]
            if(values.vessel_license && values.vessel_license.id ) attachment_ids.push(values.vessel_license.id);
            if(values.vessel_insurance && values.vessel_insurance.id ) attachment_ids.push(values.vessel_insurance.id);
            if(values.vessel_photo && values.vessel_photo.id ) attachment_ids.push(values.vessel_photo.id);
            dispatch(reservationActions.setAttachmentIds(attachment_ids));
            dispatch(reservationActions.createReservation());
        }
    });

    const onBackClicked = () => {
        if (reservationDetails && !updatReservation) {
            setUpdateReservation(true);
        }
        else {
            dispatch(reservationActions.setReservationDetails(undefined));
            dispatch(reservationActions.setForceLogin(false));
            history.go(-1);
        }

    }

    const handleConfirmPayment = () => {
        if (!extended) {
            dispatch(reservationActions.extendTimer());
        }
        dispatch(reservationActions.startTimer());
        submitRef.current && submitRef.current();
    }

    const totalToPay = useMemo(() => {
        let total = proposal?.price?.total_to_pay ?? 0;
        if(usePopeyeBalance && proposal?.price?.popeye_discount?.total_discount_via_points &&
            proposal?.price?.popeye_discount?.total_points_to_redeem) {
            return total - proposal?.price?.popeye_discount?.total_discount_via_points;
        }
        return total;
    }, [proposal, usePopeyeBalance]);
    return (<PageWrapper>
        <Elements
            options={stripeElementsOptions(payment?.stripe_client_secret)}
            stripe={stripePromise}
        >
            <ContentWrapper>
                <ReservationHeader onBackClicked={onBackClicked} forceTimer={forceTimer}/>
                <ReservationSection>
                    {
                        (reservationDetails && !updatReservation) ?
                            (finished && totalToPay === 0 ? '' : <ReservationPayment submitRef={submitRef} />)
                            :
                            <ReservationForm
                                formik={formik}
                                requirements={requirements}
                                additionalRequirements={additionalRequirements}
                                focusOnFirst={focusOnFirst}
                                setFocusOnFirst={setFocusOnFirst}
                                submitted={submitted}
                                refreshData={refreshData}
                            />
                    }
                    <ReservationSummary
                        startDate={startDate}
                        endDate={endDate}
                        vesselLength={vesselLength}
                        vesselBeam={vesselBeam}
                        vesselDraft={vesselDraft}
                        vesselType={requirements.vesselType ? vesselType : undefined}
                        hullType={requirements.hullType ? hullType : undefined}
                        natureOfTravel={requirements.natureOfTravel ? natureOfTravel : undefined}
                        marina={marina}
                        marinaData={marinaData}
                        proposal={proposal}
                        long_term_reservation ={ contract_id? true: false}
                        usePopeyeBalance={usePopeyeBalance}
                        setUsePopeyeBalance={enabled => {
                            dispatch(reservationActions.setUsePopeyeBalance(enabled));
                            dispatch(reservationActions.reloadReservationProposals());
                        }}
                        totalToPay={totalToPay}
                        disableChanges={reservationDetails && !updatReservation}
                    />
                </ReservationSection>
                {(reservationDetails && !updatReservation) && <TermsRow>
                    <Trans i18nKey="TERMS_AND_CONDITIONS">
                        a<a href="https://images.pickapier.com/data/terms_of_service.pdf" target="_blank">b</a><a
                        href="https://images.pickapier.com/data/privacy_policy.pdf" target="_blank">d</a>
                    </Trans>
                </TermsRow>}
                {finished && totalToPay === 0 ? '' : ((reservationDetails && !updatReservation) ?
                    <MainButton
                        variant="green"
                        label={t('CONFIRM_AND_PAY', {
                            currency: marina?.marina_settings?.currency,
                            amount: formatPriceNoCurrency(`${proposal?.price?.deposit ? proposal.price.deposit :  totalToPay}`)
                        })}
                        onClick={() => {
                            handleConfirmPayment()
                        }}
                    />
                    :
                    <MainButton
                        variant={ totalToPay === 0 ? "green" : undefined }
                        label={totalToPay === 0 ? t('CONFIRM_RESERVATION') : t('CONTINUE_TO_PAYMENT')}
                        onClick={() => {
                            formik.submitForm();
                            setFocusOnFirst(true);
                        }}
                    />)
                }
                <PoweredByPickapier/>
                <ReservationSuccessDialog
                    open={((window.location.pathname.endsWith(`${Routes.Finished}`) || window.location.hash.endsWith(`${Routes.Finished}`)) && finished && !!!reservationError) ||
                        (!!reservationDetails && !!reservationDetails && isRequest && !!!reservationError) || (finished && totalToPay === 0)}
                    redirectUrl={redirectUrlProvided || !reservationDetails ? (redirectUrl || window.location.origin) : `${window.location.origin}/${i18n.language}/reservation/${reservationDetails.id}`}
                    onClose={() =>{
                        dispatch(reservationActions.clear());
                    }} />
                <ReservationFailureDialog
                    open={cardError}
                    onClose={() => {dispatch(reservationActions.setCardError(false));}}
                    onSendRequest={() => { if(mail) {
                        dispatch(reservationActions.sendBookingRequest(mail));
                        dispatch(reservationActions.setCardError(false));
                    } }}
                    redirectUrl={redirectUrl || window.location.origin}
                    labels={{Subtitle: 'PAYMENT_FAILURE_TITLE', Text1: 'PAYMENT_FAILURE_INFO', Text2: 'PAYMENT_FAILURE_INFO_2', onClose: 'TRY_ANOTHER_CREDIT_CARD'} }
                />
                <ReservationFailureDialog
                    open={reservationError === 'RESERVATION_DUPLICATE_DATA'}
                    onClose={() => {dispatch(reservationActions.clear());
                                    history.go(-1)}}
                    redirectUrl={ '#'}
                    labels={{Subtitle: 'Reservation Conflict!', Text1: " It appears there's a scheduling conflict for the dates you've chosen.", Text2:" Please consider adjusting your reservation dates or review your existing bookings under 'My Trips'. ", onClose: 'Try other dates'} }
                />
            </ContentWrapper>
        </Elements>
    </PageWrapper>)
}

export default Reservation;
